import { z } from 'zod';
import {
  DocumentMetadataSchema,
  FieldRoleSchema,
  IdSchema,
  IsoDateTimeSchema,
  LogicalDataTypeSchema,
  MetadataSchema,
  PluginExtensionIdSchema,
  SafeKeySchema,
  ScalarValueSchema,
  SchemaVersionSchema,
  SemanticTypeSchema,
} from './common.js';

/** Conectores integrados previstos. Los plugins usan identificadores `plugin:<id>`. */
export const BUILTIN_CONNECTOR_IDS = [
  'postgresql',
  'sqlserver',
  'databricks',
  'mysql',
  'excel',
  'csv',
  'txt',
  'json',
  'parquet',
  'rest-api',
] as const;
export type BuiltinConnectorId = (typeof BUILTIN_CONNECTOR_IDS)[number];

/** Conectores integrados cuyas opciones aun no tienen esquema tipado propio. */
export const UNTYPED_BUILTIN_CONNECTOR_IDS = [
  'sqlserver',
  'databricks',
  'mysql',
  'txt',
  'json',
  'rest-api',
] as const satisfies readonly BuiltinConnectorId[];

export const ConnectorIdSchema = z.union([z.enum(BUILTIN_CONNECTOR_IDS), PluginExtensionIdSchema]);
export type ConnectorId = z.infer<typeof ConnectorIdSchema>;

/**
 * Ubicacion de un archivo o recurso (ruta local, `s3://`, `https://`...). Se rechazan
 * URIs con credenciales embebidas (`https://usuario:clave@host`).
 */
export const LocationSchema = z
  .string()
  .min(1)
  .max(2048)
  .regex(
    /^(?![A-Za-z][A-Za-z0-9+.-]*:\/\/[^/?#]*@)[\s\S]+$/,
    'La ubicacion no puede incluir credenciales; use secretRefs',
  );

/**
 * Referencia a un secreto en un almacen seguro. El contrato nunca transporta el valor:
 * la plataforma lo resuelve en el backend en el momento de conectar.
 */
export const SecretRefSchema = z.strictObject({
  /** Almacen que resuelve el secreto (variables de entorno, llavero del SO, vault...). */
  store: IdSchema,
  key: z.string().min(1).max(256),
});
export type SecretRef = z.infer<typeof SecretRefSchema>;

/** Nombre del parametro del conector (`password`, `token`...) -> referencia al secreto. */
export const SecretRefsSchema = z.record(
  z.string().regex(/^[A-Za-z][A-Za-z0-9_.-]{0,63}$/),
  SecretRefSchema,
);

/** Rango de celdas en notacion A1 (`A1`, `B2:F200`). */
export const A1RangeSchema = z
  .string()
  .regex(/^[A-Z]{1,3}[1-9]\d*(?::[A-Z]{1,3}[1-9]\d*)?$/, 'Rango A1 invalido');

export const PostgresOptionsSchema = z.strictObject({
  host: z.string().min(1).max(255),
  port: z.number().int().min(1).max(65535).default(5432),
  database: z.string().min(1).max(128),
  schema: z.string().min(1).max(128).optional(),
  /** El usuario no es un secreto; la contrasena va en `secretRefs.password`. */
  user: z.string().min(1).max(128).optional(),
  sslMode: z.enum(['disable', 'prefer', 'require', 'verify-ca', 'verify-full']).default('prefer'),
  applicationName: z.string().min(1).max(64).optional(),
});

export const CsvOptionsSchema = z.strictObject({
  location: LocationSchema,
  delimiter: z.string().length(1).default(','),
  quote: z.string().length(1).default('"'),
  /** Caracter de escape dentro de campos entrecomillados; si se omite, la comilla se duplica. */
  escape: z.string().length(1).optional(),
  encoding: z.string().min(1).max(32).default('utf-8'),
  hasHeader: z.boolean().default(true),
  decimalSeparator: z.enum(['.', ',']).default('.'),
  dateFormat: z.string().min(1).max(64).optional(),
});

export const ExcelOptionsSchema = z.strictObject({
  location: LocationSchema,
  /** Hojas a exponer; si se omite se exponen todas. */
  sheets: z.array(z.string().min(1).max(128)).optional(),
  headerRow: z.number().int().min(1).default(1),
  /** Rango en notacion A1 (`A1:F200`) cuando los datos no ocupan toda la hoja. */
  range: A1RangeSchema.optional(),
});

export const ParquetOptionsSchema = z.strictObject({
  /** Admite patrones glob para conjuntos particionados (`data/year=*\/*.parquet`). */
  location: LocationSchema,
  hivePartitioning: z.boolean().default(false),
});

/** Opciones de conectores sin esquema tipado: claves seguras y valores JSON. */
export const GenericConnectorOptionsSchema = MetadataSchema;

const dataSourceBase = {
  schemaVersion: SchemaVersionSchema,
  id: IdSchema,
  name: z.string().min(1).max(200),
  description: z.string().max(2000).optional(),
  secretRefs: SecretRefsSchema.default({}),
  metadata: DocumentMetadataSchema.optional(),
};

/**
 * Fuente de datos: DE DONDE vienen los datos. Contiene metadatos de conexion no
 * sensibles y referencias a secretos, nunca contrasenas, tokens ni connection strings.
 * Es independiente del Semantic Model.
 */
export const DataSourceSchema = z.union([
  z.discriminatedUnion('connectorId', [
    z.strictObject({
      ...dataSourceBase,
      connectorId: z.literal('postgresql'),
      options: PostgresOptionsSchema,
    }),
    z.strictObject({ ...dataSourceBase, connectorId: z.literal('csv'), options: CsvOptionsSchema }),
    z.strictObject({
      ...dataSourceBase,
      connectorId: z.literal('excel'),
      options: ExcelOptionsSchema,
    }),
    z.strictObject({
      ...dataSourceBase,
      connectorId: z.literal('parquet'),
      options: ParquetOptionsSchema,
    }),
    z.strictObject({
      ...dataSourceBase,
      connectorId: z.enum(UNTYPED_BUILTIN_CONNECTOR_IDS),
      options: GenericConnectorOptionsSchema,
    }),
  ]),
  z.strictObject({
    ...dataSourceBase,
    connectorId: PluginExtensionIdSchema,
    options: GenericConnectorOptionsSchema,
  }),
]);
export type DataSource = z.infer<typeof DataSourceSchema>;
export type DataSourceInput = z.input<typeof DataSourceSchema>;

// -----------------------------------------------------------------------------
// Descubrimiento automatico (perfilado de esquema)
// -----------------------------------------------------------------------------

export const DISCOVERED_OBJECT_KINDS = ['table', 'view', 'file', 'sheet', 'resource'] as const;
export const DiscoveredObjectKindSchema = z.enum(DISCOVERED_OBJECT_KINDS);
export type DiscoveredObjectKind = z.infer<typeof DiscoveredObjectKindSchema>;

/**
 * Localizador de un objeto fisico. `namespace` evita colisiones entre `esquema.tabla`
 * de una base y `libro/hoja` o `archivo` de una fuente de archivos.
 */
export const ObjectLocatorSchema = z.strictObject({
  namespace: z.array(z.string().min(1)).optional(),
  name: z.string().min(1),
});
export type ObjectLocator = z.infer<typeof ObjectLocatorSchema>;

/**
 * Profundidad del descubrimiento: `objects` solo lista objetos, `columns` agrega el
 * esquema y `profile` ademas lee datos (estadisticas y ejemplos). Permite separar
 * listado y perfilado en fuentes grandes.
 */
export const DISCOVERY_DEPTHS = ['objects', 'columns', 'profile'] as const;
export const DiscoveryDepthSchema = z.enum(DISCOVERY_DEPTHS);
export type DiscoveryDepth = z.infer<typeof DiscoveryDepthSchema>;

export const DiscoveryScopeSchema = z.strictObject({
  kinds: z.array(DiscoveredObjectKindSchema).optional(),
  /** Esquemas o contenedores seleccionados (`namespace` unido con `.`). */
  namespaces: z.array(z.string().min(1)).optional(),
  /** Tablas, hojas o archivos seleccionados. */
  objects: z.array(ObjectLocatorSchema).optional(),
});
export type DiscoveryScope = z.infer<typeof DiscoveryScopeSchema>;

/** Opciones de un descubrimiento. Todas tienen valores por defecto conservadores. */
export const DiscoveryOptionsSchema = z.strictObject({
  depth: DiscoveryDepthSchema.default('profile'),
  scope: DiscoveryScopeSchema.prefault({}),
  /** Maximo de objetos a procesar; el resto se reporta como truncamiento. */
  maxObjects: z.number().int().positive().max(100_000).default(500),
  /** Filas leidas por objeto para perfilar. */
  sampleRows: z.number().int().positive().max(1_000_000).default(10_000),
  includeStatistics: z.boolean().default(true),
  includeSampleValues: z.boolean().default(true),
  maxSampleValues: z.number().int().min(1).max(50).default(10),
  includeRelationships: z.boolean().default(true),
  /** Si es `false`, no se leen datos aunque `depth` sea `profile`. */
  includeProfiling: z.boolean().default(true),
  /** Conteo exacto de filas aunque requiera recorrer todo el objeto. */
  exactRowCount: z.boolean().default(false),
  /** Detectar formato de archivo (delimitador, encoding, encabezado...). */
  detectFileFormat: z.boolean().default(true),
});
export type DiscoveryOptions = z.infer<typeof DiscoveryOptionsSchema>;
export type DiscoveryOptionsInput = z.input<typeof DiscoveryOptionsSchema>;

/**
 * Procedencia de las estadisticas: `data` (calculadas leyendo filas), `metadata`
 * (declaradas por el formato, p. ej. Parquet) o `catalog` (estadisticas del motor).
 */
export const PROFILING_SOURCES = ['data', 'metadata', 'catalog'] as const;

export const ProfilingInfoSchema = z.strictObject({
  /** `true` si solo se leyo una muestra de las filas. */
  sampled: z.boolean(),
  sampleRows: z.number().int().nonnegative().optional(),
  /** `true` si los valores son estimaciones (catalogo, tope de distintos...). */
  approximate: z.boolean().optional(),
  source: z.enum(PROFILING_SOURCES).optional(),
});
export type ProfilingInfo = z.infer<typeof ProfilingInfoSchema>;

const IssueCodeSchema = z
  .string()
  .max(64)
  .regex(/^[a-z][a-z0-9-]*$/);

/** Patron observado en los valores de una columna (`currency-symbol`, `leading-zeros`...). */
export const ValuePatternSchema = z.strictObject({
  code: IssueCodeSchema,
  /** Proporcion de valores no nulos que cumplen el patron. */
  ratio: z.number().min(0).max(1),
});
export type ValuePattern = z.infer<typeof ValuePatternSchema>;

/**
 * Estadisticas de una columna. No asumir exactitud: `profiling` indica si se
 * calcularon sobre una muestra, si son aproximadas y de donde provienen.
 */
export const ColumnStatisticsSchema = z.strictObject({
  rowCount: z.number().int().nonnegative().optional(),
  nullCount: z.number().int().nonnegative().optional(),
  /** Cadenas vacias o solo espacios (se cuentan aparte de los nulos). */
  emptyCount: z.number().int().nonnegative().optional(),
  distinctCount: z.number().int().nonnegative().optional(),
  min: ScalarValueSchema.optional(),
  max: ScalarValueSchema.optional(),
  minLength: z.number().int().nonnegative().optional(),
  maxLength: z.number().int().nonnegative().optional(),
  /** Valores no nulos por tipo inferido; revela columnas mixtas. */
  typeCounts: z.partialRecord(LogicalDataTypeSchema, z.number().int().nonnegative()).optional(),
  patterns: z.array(ValuePatternSchema).max(20).optional(),
  sampleValues: z.array(ScalarValueSchema).max(50).optional(),
  /** `true` si se omitieron ejemplos y min/max por tratarse de una columna sensible. */
  redacted: z.boolean().optional(),
  profiling: ProfilingInfoSchema.optional(),
});
export type ColumnStatistics = z.infer<typeof ColumnStatisticsSchema>;

export const DiscoveredColumnSchema = z.strictObject({
  /** Id interno determinista; distinto del nombre visible. */
  id: IdSchema.optional(),
  /** Nombre fisico unico dentro del objeto (deduplicado si la fuente repite nombres). */
  name: z.string().min(1),
  /** Nombre tal como aparece en la fuente cuando difiere de `name` (duplicado o vacio). */
  originalName: z.string().optional(),
  ordinal: z.number().int().nonnegative().optional(),
  nativeType: z.string().min(1),
  logicalType: LogicalDataTypeSchema,
  nullable: z.boolean(),
  /** `declared` por la fuente, `observed` en los datos o `unknown`. */
  nullableSource: z.enum(['declared', 'observed', 'unknown']).optional(),
  statistics: ColumnStatisticsSchema.optional(),
  /** Sugerencias del perfilador; el usuario decide si las acepta. */
  suggestedRole: FieldRoleSchema.optional(),
  suggestedSemanticType: SemanticTypeSchema.optional(),
});
export type DiscoveredColumn = z.infer<typeof DiscoveredColumnSchema>;

/** Restricciones DECLARADAS por la fuente. Las inferidas se expresan como recomendaciones. */
export const TableConstraintsSchema = z.strictObject({
  primaryKey: z.array(z.string().min(1)).optional(),
  unique: z.array(z.array(z.string().min(1)).min(1)).default([]),
});
export type TableConstraints = z.infer<typeof TableConstraintsSchema>;

/** Formato detectado (o configurado) de un objeto basado en archivo. */
export const DetectedFileFormatSchema = z.discriminatedUnion('format', [
  z.strictObject({
    format: z.literal('csv'),
    delimiter: z.string().length(1),
    quote: z.string().length(1),
    escape: z.string().length(1).optional(),
    encoding: z.string().min(1).max(32),
    bom: z.boolean().optional(),
    hasHeader: z.boolean(),
    headerConfidence: z.number().min(0).max(1).optional(),
    decimalSeparator: z.enum(['.', ',']).optional(),
    /** `true` si los valores se detectaron; `false` si se tomaron de las opciones. */
    detected: z.boolean(),
  }),
  z.strictObject({
    format: z.literal('excel'),
    workbook: z.string().min(1),
    sheet: z.string().min(1),
    headerRow: z.number().int().min(1).optional(),
    range: A1RangeSchema.optional(),
    hidden: z.boolean().optional(),
  }),
  z.strictObject({
    format: z.literal('parquet'),
    rowGroups: z.number().int().nonnegative(),
    createdBy: z.string().max(512).optional(),
    compression: z.array(z.string().min(1)).optional(),
    keyValueMetadata: z.record(SafeKeySchema, z.string().max(4096)).optional(),
  }),
]);
export type DetectedFileFormat = z.infer<typeof DetectedFileFormatSchema>;

/**
 * Aviso o error de descubrimiento. Los mensajes son diagnosticos y nunca incluyen
 * secretos ni cadenas de conexion.
 */
export const DiscoveryIssueSchema = z.strictObject({
  severity: z.enum(['warning', 'error']),
  /** Codigo estable en kebab-case (`object-failed`, `mixed-types`, `truncated`...). */
  code: IssueCodeSchema,
  message: z.string().min(1).max(2000),
  object: ObjectLocatorSchema.optional(),
  column: z.string().min(1).optional(),
});
export type DiscoveryIssue = z.infer<typeof DiscoveryIssueSchema>;

export const DISCOVERED_OBJECT_STATUSES = ['ok', 'partial', 'failed', 'skipped'] as const;

export const DiscoveredTableSchema = z.strictObject({
  /** Id interno determinista; distinto del nombre visible. */
  id: IdSchema.optional(),
  name: z.string().min(1),
  namespace: z.array(z.string().min(1)).optional(),
  kind: DiscoveredObjectKindSchema,
  status: z.enum(DISCOVERED_OBJECT_STATUSES).default('ok'),
  issues: z.array(DiscoveryIssueSchema).default([]),
  rowCountEstimate: z.number().int().nonnegative().optional(),
  /** Conteo exacto, solo si la fuente lo declara o se recorrio todo el objeto. */
  rowCount: z.number().int().nonnegative().optional(),
  profiling: ProfilingInfoSchema.optional(),
  constraints: TableConstraintsSchema.optional(),
  fileFormat: DetectedFileFormatSchema.optional(),
  columns: z.array(DiscoveredColumnSchema),
});
export type DiscoveredTable = z.infer<typeof DiscoveredTableSchema>;
export type DiscoveredTableInput = z.input<typeof DiscoveredTableSchema>;

export const ColumnLocatorSchema = z.strictObject({
  namespace: z.array(z.string().min(1)).optional(),
  table: z.string().min(1),
  column: z.string().min(1),
});
export type ColumnLocator = z.infer<typeof ColumnLocatorSchema>;

/**
 * Evidencia de relacion observada durante el descubrimiento (FK declarada o
 * coincidencia). La propuesta de modelado es una `ModelingRecommendation` de tipo
 * `relationship`.
 */
export const SuggestedRelationshipSchema = z.strictObject({
  from: ColumnLocatorSchema,
  to: ColumnLocatorSchema,
  /** Confianza entre 0 y 1 de la heuristica que propuso la relacion. */
  confidence: z.number().min(0).max(1),
  reason: z.enum(['foreign-key', 'name-match', 'value-overlap']),
});
export type SuggestedRelationship = z.infer<typeof SuggestedRelationshipSchema>;

export const DiscoverySummarySchema = z.strictObject({
  found: z.number().int().nonnegative(),
  processed: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
});
export type DiscoverySummary = z.infer<typeof DiscoverySummarySchema>;

/**
 * Resultado del descubrimiento: QUE objetos y columnas existen fisicamente y que
 * caracteristicas tienen sus datos. No es un Dataset ni un Semantic Model.
 */
export const SchemaDiscoveryResultSchema = z.strictObject({
  schemaVersion: SchemaVersionSchema,
  dataSourceId: IdSchema,
  connectorId: ConnectorIdSchema.optional(),
  discoveredAt: IsoDateTimeSchema,
  /** `partial` si algun objeto fallo o se omitio; `failed` si ninguno se pudo procesar. */
  status: z.enum(['complete', 'partial', 'failed']).default('complete'),
  /** Opciones efectivas con las que se ejecuto. */
  options: DiscoveryOptionsSchema.optional(),
  summary: DiscoverySummarySchema.optional(),
  /** `true` si se alcanzo `maxObjects` y quedaron objetos sin listar. */
  truncated: z.boolean().default(false),
  issues: z.array(DiscoveryIssueSchema).default([]),
  durationMs: z.number().nonnegative().optional(),
  tables: z.array(DiscoveredTableSchema),
  suggestedRelationships: z.array(SuggestedRelationshipSchema).default([]),
});
export type SchemaDiscoveryResult = z.infer<typeof SchemaDiscoveryResultSchema>;
export type SchemaDiscoveryResultInput = z.input<typeof SchemaDiscoveryResultSchema>;

/** Vista previa acotada de un objeto (maximo 100 filas). No es un QueryResult. */
export const DataPreviewSchema = z
  .strictObject({
    dataSourceId: IdSchema,
    object: ObjectLocatorSchema,
    columns: z.array(
      z.strictObject({ name: z.string().min(1), logicalType: LogicalDataTypeSchema }),
    ),
    rows: z.array(z.array(ScalarValueSchema)).max(100),
    truncated: z.boolean(),
  })
  .superRefine((preview, ctx) => {
    preview.rows.forEach((row, r) => {
      if (row.length !== preview.columns.length) {
        ctx.addIssue({
          code: 'custom',
          path: ['rows', r],
          message: `La fila tiene ${String(row.length)} valores y hay ${String(preview.columns.length)} columnas.`,
        });
      }
    });
  });
export type DataPreview = z.infer<typeof DataPreviewSchema>;
